import { type ReactNode, useCallback } from 'react';
import { useCreateAndRenameFolder } from '@frameio/assets/operations';
import type { PlanFeatureInput } from '@frameio/graphql-client/generated';
import { useFolderPermissionSet } from '@frameio/permissions';
import { useFeatureGatePlanSuggestion } from '@frameio/plan-management/feature-gate/hooks';
import { HiddenInput } from '@frameio/shared/components';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import type { TrackingSourceType } from '@frameio/shared/segment/hooks/utils/trackingUtils';
import { useUploaderButton, useUploadSelect } from '@frameio/transfers/hooks';
import { MenuButton, MenuGroup, Tooltip } from '@frameio/vapor';
import {
  IconCloudArrow,
  IconFolderAdd,
  IconFolderLocked,
  IconGlobe,
} from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/useCreateAssetMenu';

// Feature flag constant for restricted folders functionality
const RESTRICTED_FOLDERS: PlanFeatureInput = 'RESTRICTED_FOLDERS';

export type useCreateAssetMenuOpts = {
  disableFolderButtons: boolean;
  disabledFolderButtonTooltip?: string;
  enableAddWebReviewButton: boolean;
  folderId: string;
  onAddWebReviewPress?: VoidFunction;
  /** Allow overriding the default createFolder call */
  onCreateFolder?: (parentId: string, restricted: boolean) => void;
  projectId: string;
  trackingSource: TrackingSourceType;
};

type Buttons =
  | {
      buttons: ReactNode;
      input: ReactNode;
    }
  | undefined;

export function useCreateAssetMenu({
  disableFolderButtons,
  disabledFolderButtonTooltip,
  enableAddWebReviewButton,
  folderId,
  onAddWebReviewPress,
  onCreateFolder,
  projectId,
  trackingSource,
}: useCreateAssetMenuOpts): Buttons {
  // Check if restricted folders feature is enabled for the current plan
  const restrictedFolderFeature =
    useFeatureGatePlanSuggestion(RESTRICTED_FOLDERS);
  const isRestrictedFoldersEnabled =
    restrictedFolderFeature?.includedInCurrentPlan;

  const { canCreateChildren, canEditUserPermissions } = useFolderPermissionSet({
    checks: ['canCreateChildren', 'canEditUserPermissions'],
    folderId,
  });

  const track = useTrackUnsafe();
  const createFolder = useCreateAndRenameFolder();

  const { inputOpenFileRef, uploadSelect } = useUploadSelect();

  const { fileSelect } = useUploaderButton({
    folderId,
    projectId,
  });

  // Determine if user can create restricted folders (requires feature flag and permissions)
  const canCreateRestrictedFolder =
    isRestrictedFoldersEnabled && canEditUserPermissions;

  const handleNewFolderPress = useCallback(
    (restricted = false) =>
      () => {
        track('folder-created', {
          is_restricted: restricted,
          source: trackingSource,
        });

        const parentId = folderId || projectId;

        if (onCreateFolder) {
          onCreateFolder(parentId, restricted);
          return;
        }

        createFolder({
          parentId: folderId || projectId,
          projectId,
          restricted,
        });
      },
    [createFolder, folderId, onCreateFolder, projectId, track, trackingSource]
  );

  const handleFileUploadSelect = useCallback(() => {
    uploadSelect();
  }, [uploadSelect]);

  const handleFolderUploadSelect = useCallback(() => {
    uploadSelect(true);
  }, [uploadSelect]);

  const {
    addWebReview,
    newFolder,
    newRestrictedFolder,
    uploadFile,
    uploadFolder,
  } = TEXT;

  if (!canCreateChildren) return undefined;

  const buttons = (
    <>
      <MenuGroup>
        <Tooltip
          disabled={!disableFolderButtons}
          title={disabledFolderButtonTooltip}
        >
          <span>
            <MenuButton
              disabled={disableFolderButtons}
              iconBefore={<IconFolderAdd />}
              onPress={handleNewFolderPress()}
            >
              {newFolder}
            </MenuButton>
          </span>
        </Tooltip>
        {canCreateRestrictedFolder && (
          <Tooltip
            disabled={!disableFolderButtons}
            title={disabledFolderButtonTooltip}
          >
            <span>
              <MenuButton
                disabled={disableFolderButtons}
                iconBefore={<IconFolderLocked />}
                onPress={handleNewFolderPress(true)}
              >
                {newRestrictedFolder}
              </MenuButton>
            </span>
          </Tooltip>
        )}
      </MenuGroup>
      <MenuGroup>
        <MenuButton
          color="primary"
          iconBefore={<IconCloudArrow />}
          onPress={handleFileUploadSelect}
        >
          {uploadFile}
        </MenuButton>
        <MenuButton
          iconBefore={<IconCloudArrow />}
          onPress={handleFolderUploadSelect}
        >
          {uploadFolder}
        </MenuButton>
        {enableAddWebReviewButton && (
          <MenuButton iconBefore={<IconGlobe />} onPress={onAddWebReviewPress}>
            {addWebReview}
          </MenuButton>
        )}
      </MenuGroup>
    </>
  );

  const input = (
    <HiddenInput
      ref={inputOpenFileRef}
      aria-label={uploadFile}
      data-testid="create-asset-menu--upload-input"
      multiple={true}
      name="files"
      onChange={fileSelect}
      tabIndex={-1}
      type="file"
    />
  );

  return {
    buttons,
    input,
  };
}

TEXT.DEFINE({
  addWebReview: 'Add Web Review',
  newFolder: 'New Folder',
  newRestrictedFolder: 'New Restricted Folder',
  uploadFile: 'Upload Asset',
  uploadFolder: 'Upload Folder',
});
